import * as React from 'react'
import { useForm } from '@inertiajs/react'
import { Button } from '@/shared/ui/button'
import { Textarea } from '@/shared/ui/textarea'
import { t } from '@/shared/lib/i18n'
import { complianceCorrectiveActionUpdatePath } from '@/shared/lib/routes'
import type { WebCorrectiveAction } from '@/shared/types/web'

const MAX_COMMENT_LENGTH = 5000

interface Props {
  correctiveAction: WebCorrectiveAction
  complianceId: number
  onCancel: () => void
}

const CorrectiveActionEditForm = ({ correctiveAction, complianceId, onCancel }: Props) => {
  const form = useForm({ comment: correctiveAction.comment ?? '' })

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    form.patch(complianceCorrectiveActionUpdatePath(complianceId, correctiveAction.id), {
      onSuccess: onCancel,
    })
  }

  const isInvalid =
    form.data.comment.trim().length === 0 || form.data.comment.length > MAX_COMMENT_LENGTH

  return (
    <form onSubmit={handleSubmit} className="space-y-2">
      <Textarea
        value={form.data.comment}
        onChange={(e) => form.setData('comment', e.target.value)}
        rows={3}
        placeholder={t('compliance.corrective_actions.edit_placeholder')}
      />
      <p className="text-xs text-muted-foreground">
        {t('compliance.corrective_actions.char_count').replace(
          '{{count}}',
          String(form.data.comment.length),
        )}
      </p>
      {(form.errors as Record<string, string | undefined>).base && (
        <p className="text-sm text-destructive">
          {(form.errors as Record<string, string | undefined>).base}
        </p>
      )}
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={form.processing || isInvalid}>
          {form.processing ? '…' : t('compliance.corrective_actions.save_button')}
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>
          {t('compliance.corrective_actions.cancel_button')}
        </Button>
      </div>
    </form>
  )
}

export default CorrectiveActionEditForm
